import { useEffect, useMemo, useRef, useState } from 'react';
import { ChefHat, CheckCircle2, ShoppingCart, Maximize2, Minimize2, Clock, RefreshCw, Wifi } from 'lucide-react';
import { useBranch } from '../context/branch-context';
import { listPedidos, type Pedido } from '../../api/pedidos';
import { useRealtime } from '../hooks/use-realtime';
import { useTextos } from '../context/regional-context';
import { textosDashboard, type ClaveDashboard } from '../../i18n/modulos/dashboard';
import { localeFechas } from '../../i18n/actual';

interface DashboardProps {
  branch: string;
}

/**
 * Dashboard tipo McDonald's — vista de cocina.
 * Muestra en tiempo real:
 *   - "En preparación" — pedidos que la cocina debe atender
 *   - "Listos" — pedidos que esperan al mesero
 *   - "Entregados" — pedidos que fueron entregados hace menos de 10 min (acuse de recibo)
 *
 * El botón pantalla completa la oculta la sidebar y top bar para usar en una
 * pantalla pública en cocina.
 */
// Renombrado el 31-may-2026: este componente sigue intacto y es la vista de
// cocina. Ahora se invoca desde el nuevo `Dashboard` ejecutivo (al final del
// archivo) mediante un botón "Vista cocina". La vieja función Dashboard ahora
// se llama KitchenDashboard para reflejar su rol real.
export function KitchenDashboard({ branch }: DashboardProps) {
  const { currentBranchId } = useBranch();
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorPedidos, setErrorPedidos] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [, forceTick] = useState(0); // re-render cada segundo para que los tiempos avancen
  const tx = useTextos(textosDashboard);

  // Re-render cada 10 segundos (para "hace X minutos" y filtro de 10 min)
  useEffect(() => {
    const t = setInterval(() => forceTick(x => x + 1), 10_000);
    return () => clearInterval(t);
  }, []);

  const cargar = async () => {
    if (!currentBranchId) return;
    setLoading(true);
    try {
      // Pedimos activos + entregados recientes (los entregados de >10min se filtran en el cliente)
      const data = await listPedidos({ sede_id: currentBranchId });
      setPedidos(data);
    } catch (err) {
      // En cocina una lista vacia se lee como "no hay nada que preparar".
      // Si la carga fallo hay que decirlo, no mostrar una pantalla limpia.
      console.error('Dashboard load error', err);
      setErrorPedidos((err as Error)?.message || tx('errorDesconocido'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, [currentBranchId]);

  useRealtime(
    ['pedido_creado', 'pedido_actualizado'],
    () => cargar()
  );

  // ---------- Fullscreen API ----------
  const toggleFullscreen = async () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      await document.exitFullscreen();
      setIsFullscreen(false);
    } else {
      await el.requestFullscreen();
      setIsFullscreen(true);
    }
  };

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // ---------- Clasificación de pedidos ----------
  const enPreparacion = useMemo(
    () => pedidos.filter(p => p.estado === 'abierto' || p.estado === 'en_preparacion'),
    [pedidos]
  );
  const listos = useMemo(
    () => pedidos.filter(p => p.estado === 'listo'),
    [pedidos]
  );
  const entregadosRecientes = useMemo(() => {
    const now = Date.now();
    return pedidos.filter(p => {
      if (p.estado !== 'entregado') return false;
      const t = new Date((p as any).updated_at || p.created_at).getTime();
      return now - t < 10 * 60 * 1000; // <10 min
    });
  }, [pedidos]);

  // ---------- Helpers de tiempo ----------
  const tiempoTranscurrido = (iso: string) => {
    const min = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
    if (min < 1) return tx('ahora');
    if (min === 1) return '1 min';
    return `${min} min`;
  };

  return (
    <div
      ref={containerRef}
      className={`${isFullscreen ? 'min-h-screen p-6 lg:p-10 overflow-y-auto' : 'p-4 sm:p-6 lg:p-10 pb-32 max-w-screen-2xl mx-auto'}`}
      style={isFullscreen ? { background: 'var(--bg-app)' } : undefined}
    >
      {errorPedidos && (
        <div
          className="mb-4 p-3"
          style={{
            background: 'var(--danger-bg)',
            color: 'var(--danger-ink)',
            border: '1.5px solid color-mix(in srgb, var(--danger) 25%, var(--line))',
            borderRadius: 'var(--r-sm)',
            fontWeight: 600,
          }}
        >
          {tx('errorPedidos', { error: errorPedidos })}
        </div>
      )}

      {/* Header — design handoff */}
      <div className="flex items-start justify-between mb-6 sm:mb-7 flex-wrap gap-3">
        <div>
          <h1 className={`${isFullscreen ? 'sk-display' : 'sk-h1'} flex items-center gap-3 flex-wrap`} style={{ color: 'var(--ink)' }}>
            {tx('pedidosEnVivo')}
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[13px] font-bold"
              style={{
                background: 'var(--ok-bg)',
                color: 'var(--ok-ink)',
              }}
            >
              <Wifi className="w-3 h-3" /> {tx('enVivo')}
            </span>
          </h1>
          <p className={`mt-1 ${isFullscreen ? 'text-[17px]' : 'text-[15px]'} font-semibold`} style={{ color: 'var(--muted-ink)' }}>{branch}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={cargar}
            disabled={loading}
            className="sk-btn sk-btn--ghost disabled:opacity-60"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            {tx('recargar')}
          </button>
          <button
            onClick={toggleFullscreen}
            className="sk-btn sk-btn--primary"
          >
            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            {isFullscreen ? tx('salir') : tx('pantallaCompleta')}
          </button>
        </div>
      </div>

      <div className={`grid gap-4 sm:gap-5 lg:gap-6 ${isFullscreen ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1 lg:grid-cols-3'}`}>
        {/* ============ EN PREPARACIÓN ============ */}
        <Column
          title={tx('colEnPreparacion')}
          count={enPreparacion.length}
          color="amber"
          icon={ChefHat}
          isFullscreen={isFullscreen}
        >
          {enPreparacion.length === 0 ? (
            <EmptyState text={tx('sinPedidosCocina')} />
          ) : (
            enPreparacion.map((p, idx) => (
              <OrderCard
                key={p.id}
                pedido={p}
                color="amber"
                tiempo={tiempoTranscurrido(p.created_at)}
                isFullscreen={isFullscreen}
                idx={idx}
              />
            ))
          )}
        </Column>

        {/* ============ LISTOS ============ */}
        <Column
          title={tx('colListos')}
          count={listos.length}
          color="emerald"
          icon={CheckCircle2}
          isFullscreen={isFullscreen}
        >
          {listos.length === 0 ? (
            <EmptyState text={tx('sinPedidosListos')} />
          ) : (
            listos.map((p, idx) => (
              <OrderCard
                key={p.id}
                pedido={p}
                color="emerald"
                tiempo={tiempoTranscurrido(p.created_at)}
                isFullscreen={isFullscreen}
                idx={idx}
                pulse
              />
            ))
          )}
        </Column>

        {/* ============ ENTREGADOS RECIENTES ============ */}
        <Column
          title={tx('colEntregados')}
          count={entregadosRecientes.length}
          color="primary"
          icon={ShoppingCart}
          isFullscreen={isFullscreen}
        >
          {entregadosRecientes.length === 0 ? (
            <EmptyState text={tx('sinEntregasRecientes')} />
          ) : (
            entregadosRecientes.map((p, idx) => (
              <OrderCard
                key={p.id}
                pedido={p}
                color="primary"
                tiempo={tx('hace', { tiempo: tiempoTranscurrido((p as any).updated_at || p.created_at) })}
                isFullscreen={isFullscreen}
                idx={idx}
                dimmed
              />
            ))
          )}
        </Column>
      </div>
    </div>
  );
}

// =============================================================================
// Column — usa tokens del handoff (--ok / --warn / --blue-*) + sk-label.
// =============================================================================
function Column({
  title, count, color, icon: Icon, children, isFullscreen,
}: {
  title: string;
  count: number;
  color: 'amber' | 'emerald' | 'primary';
  icon: any;
  children: any;
  isFullscreen: boolean;
}) {
  // Mapeo a tokens del design handoff (semánticos):
  //   amber   -> WARN (en preparación, esperando)
  //   emerald -> OK   (listos para entregar)
  //   primary -> BLUE (entregados / informativo)
  const tokens = {
    amber:    { tintBg: 'var(--warn-bg)',  tintInk: 'var(--warn-ink)', headerBg: 'color-mix(in srgb, var(--warn-bg) 55%, var(--surface))' },
    emerald:  { tintBg: 'var(--ok-bg)',    tintInk: 'var(--ok-ink)',   headerBg: 'color-mix(in srgb, var(--ok-bg) 55%, var(--surface))' },
    primary:  { tintBg: 'var(--blue-100)', tintInk: 'var(--blue-700)', headerBg: 'var(--blue-50)' },
  } as const;
  const t = tokens[color];

  return (
    <div
      className="overflow-hidden flex flex-col"
      style={{
        background: 'var(--surface)',
        border: '1.5px solid var(--line)',
        borderRadius: 'var(--r-lg)',
        boxShadow: 'var(--sh-md)',
      }}
    >
      <div
        className="shrink-0 px-5 py-4"
        style={{
          background: t.headerBg,
          borderBottom: '1.5px solid var(--line)',
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div
              className="p-2 flex items-center justify-center"
              style={{
                background: t.tintBg,
                color: t.tintInk,
                borderRadius: 'var(--r-sm)',
              }}
            >
              <Icon className="w-[18px] h-[18px]" />
            </div>
            <h2
              className={`sk-label`}
              style={{
                color: 'var(--ink)',
                fontSize: isFullscreen ? '15px' : '13px',
              }}
            >
              {title}
            </h2>
          </div>
          <span
            className="nums tracking-tight leading-none"
            style={{
              color: 'var(--ink)',
              fontSize: isFullscreen ? '32px' : '24px',
              fontWeight: 800,
            }}
          >
            {count}
          </span>
        </div>
      </div>
      <div className={`flex-1 ${isFullscreen ? 'p-4 space-y-3 max-h-[78vh]' : 'p-3 space-y-2.5 max-h-[70vh]'} overflow-y-auto`}>
        {children}
      </div>
    </div>
  );
}

// =============================================================================
// OrderCard — usa tokens semánticos + radii del handoff.
// =============================================================================
function OrderCard({
  pedido, color, tiempo, isFullscreen, idx, pulse, dimmed,
}: {
  pedido: Pedido;
  color: 'amber' | 'emerald' | 'primary';
  tiempo: string;
  isFullscreen: boolean;
  idx: number;
  pulse?: boolean;
  dimmed?: boolean;
}) {
  // Mapeo a tokens del handoff. Usamos color-mix para hacer fondos sutiles.
  const tokens = {
    amber:    {
      bg: 'color-mix(in srgb, var(--warn-bg) 75%, var(--surface))',
      border: 'color-mix(in srgb, var(--warn) 22%, var(--line))',
    },
    emerald:  {
      bg: 'color-mix(in srgb, var(--ok-bg) 75%, var(--surface))',
      border: 'color-mix(in srgb, var(--ok) 22%, var(--line))',
    },
    primary:  {
      bg: 'var(--blue-50)',
      border: 'color-mix(in srgb, var(--blue-500) 18%, var(--line))',
    },
  } as const;
  const t = tokens[color];
  const tx = useTextos(textosDashboard);

  const refSize = isFullscreen ? '28px' : '22px';
  const mesaSize = isFullscreen ? '18px' : '15px';

  return (
    <div
      className={`animate-apple-slide-up ${dimmed ? 'opacity-60' : ''} ${pulse ? 'animate-pulse' : ''}`}
      style={{
        background: t.bg,
        border: `1.5px solid ${t.border}`,
        borderRadius: 'var(--r-md)',
        padding: isFullscreen ? '16px 18px' : '14px',
        animationDelay: `${(idx % 6) * 50}ms`,
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div
          className="tracking-tight leading-tight"
          style={{ color: 'var(--ink)', fontSize: refSize, fontWeight: 800 }}
        >
          {pedido.referencia}
        </div>
        <div
          className="flex items-center gap-1 shrink-0"
          style={{
            color: 'var(--muted-ink)',
            fontSize: isFullscreen ? '14px' : '12.5px',
            fontWeight: 600,
          }}
        >
          <Clock className="w-3 h-3" />
          {tiempo}
        </div>
      </div>
      <div style={{ color: 'var(--ink-700)', fontSize: mesaSize, fontWeight: 600 }}>
        {pedido.mesa_nombre ? tx('mesaN', { nombre: pedido.mesa_nombre }) : pedido.grupo_nombre ? tx('grupoN', { nombre: pedido.grupo_nombre }) : tx('paraLlevar')}
      </div>
      {pedido.mesero_nombre && (
        <div
          className="mt-0.5"
          style={{
            color: 'var(--muted-ink)',
            fontSize: isFullscreen ? '14px' : '13px',
            fontWeight: 500,
          }}
        >
          {pedido.mesero_nombre}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// EmptyState — muted-ink token.
// =============================================================================
function EmptyState({ text }: { text: string }) {
  return (
    <div
      className="text-center py-10"
      style={{
        color: 'var(--muted-2)',
        fontSize: '14px',
        fontWeight: 600,
      }}
    >
      {text}
    </div>
  );
}

// =============================================================================
// Dashboard ejecutivo (vista por defecto del módulo)
// Refactor 31-may-2026 — el handoff DwFiv5nX no estaba disponible, así que
// rediseñé el Dashboard con datos reales del backend:
//   - 4 KPI cards con datos de hoy (ventas, pedidos, ticket promedio, mesas)
//   - LineChart: ventas por hora (de las ventas DIAN registradas hoy)
//   - BarChart: top 5 productos del día por unidades vendidas
//   - Pedidos por estado (pills coloreadas)
//   - Ocupación de mesas (gauge)
//   - Botón "Vista cocina" para abrir KitchenDashboard inline
// Si faltan datos del backend, cada widget muestra "Sin datos" claramente.
// =============================================================================
import {
  LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip,
  BarChart, Bar, CartesianGrid,
} from 'recharts';
import { TrendingUp, Receipt as ReceiptIcon, Users as UsersIcon2, Utensils, Eye, LayoutDashboard } from 'lucide-react';
import { useUser } from '../context/user-context';
import { fetchVentas, fetchVenta, num as vNum, type Venta } from '../../api/ventas';
import { listMesas, type Mesa } from '../../api/mesas';
import { listInventario, type InventarioItem } from '../../api/inventario';
import { ModuleHeader } from './ui/module-header';
import { AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router';
import { formatMoney, formatMoneyRedondo } from '@/lib/moneda';

interface DashProps { branch: string; }

const fmtCOP = (n: number) => formatMoneyRedondo(n);

// Estado crudo del pedido (valor del backend) → clave de su etiqueta en el
// resumen de "Pedidos activos". En español la etiqueta ES el valor (igual que antes).
const ESTADO_CRUDO: Record<string, ClaveDashboard> = {
  abierto: 'crudoAbierto',
  en_preparacion: 'crudoEnPreparacion',
  listo: 'crudoListo',
  entregado: 'crudoEntregado',
  facturado: 'crudoFacturado',
};

export function Dashboard({ branch }: DashProps) {
  const { currentBranchId } = useBranch();
  const { user } = useUser();
  const [showKitchen, setShowKitchen] = useState(false);
  const tx = useTextos(textosDashboard);

  const navigate = useNavigate();
  const [ventasHoy, setVentasHoy] = useState<Venta[]>([]);
  const [pedidosHoy, setPedidosHoy] = useState<Pedido[]>([]);
  const [mesasState, setMesasState] = useState<Mesa[]>([]);
  const [inventario, setInventario] = useState<InventarioItem[]>([]);
  const [topItems, setTopItems] = useState<Array<{ nombre: string; cantidad: number; total: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const hoyIso = useMemo(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  }, []);

  const cargar = async () => {
    if (!currentBranchId) return;
    setLoading(true); setErr(null);
    // Si el inventario no carga, devolver [] hacia que la pantalla dijera
    // "0 items bajo minimo", que se lee como "todo en orden". Ahora el fallo
    // se anota y se avisa arriba.
    let fallaInventario = false;
    try {
      const [vntRes, peds, msas, inv] = await Promise.all([
        fetchVentas({ sede_id: currentBranchId, desde: hoyIso, hasta: hoyIso, limit: 500 }),
        listPedidos({ sede_id: currentBranchId, activos: true }),
        listMesas(currentBranchId),
        listInventario(currentBranchId).catch(() => { fallaInventario = true; return []; }),
      ]);
      const vnts = vntRes.data || [];
      setVentasHoy(vnts);
      setPedidosHoy(peds);
      setMesasState(msas);
      setInventario(inv);

      // Top productos del día — pedimos los items de las primeras 25 ventas
      // (basta para tener una muestra significativa sin saturar la red).
      const muestra = vnts.slice(0, 25);
      const items: Array<{ nombre: string; cantidad: number; total: number }> = [];
      const acc = new Map<string, { cantidad: number; total: number }>();
      await Promise.all(muestra.map(async v => {
        try {
          const det = await fetchVenta(v.id);
          for (const it of (det.items || [])) {
            const nm = (it.nombre || it.descripcion || '—').toString();
            const cant = Number(it.cantidad) || 0;
            const sub  = Number(it.subtotal) || (Number(it.precio_unitario) || 0) * cant;
            const cur = acc.get(nm) || { cantidad: 0, total: 0 };
            cur.cantidad += cant; cur.total += sub;
            acc.set(nm, cur);
          }
        } catch { /* skip */ }
      }));
      acc.forEach((v, k) => items.push({ nombre: k, ...v }));
      items.sort((a, b) => b.cantidad - a.cantidad);
      setTopItems(items.slice(0, 5));
      if (fallaInventario) setErr(tx('errorInventario'));
    } catch (e: any) {
      setErr(e?.message || tx('errorDatos'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [currentBranchId]);

  useRealtime(['pedido_creado', 'pedido_actualizado'], () => cargar());

  // ---------- KPIs ----------
  const kpis = useMemo(() => {
    const ventasTot = ventasHoy.reduce((s, v) => s + vNum(v.total), 0);
    const ventasCount = ventasHoy.length;
    const ticketProm = ventasCount ? ventasTot / ventasCount : 0;
    const mesasOcupadas = mesasState.filter(m => m.estado !== 'libre' && m.estado !== 'reservada').length;
    const mesasTot = mesasState.length;
    return { ventasTot, ventasCount, ticketProm, mesasOcupadas, mesasTot, pedidosActivos: pedidosHoy.length };
  }, [ventasHoy, mesasState, pedidosHoy]);

  // ---------- Ventas por hora ----------
  const ventasPorHora = useMemo(() => {
    const buckets: Record<number, number> = {};
    for (let h = 6; h <= 23; h++) buckets[h] = 0;
    ventasHoy.forEach(v => {
      const hh = parseInt((v.hora_emision || '00:00:00').slice(0, 2), 10);
      if (hh >= 0 && hh <= 23) buckets[hh] = (buckets[hh] || 0) + vNum(v.total);
    });
    return Object.entries(buckets).map(([h, total]) => ({
      hora: `${h.padStart(2,'0')}h`,
      total,
    }));
  }, [ventasHoy]);

  // ---------- Pedidos por estado ----------
  const pedidosPorEstado = useMemo(() => {
    const acc: Record<string, number> = {};
    pedidosHoy.forEach(p => { acc[p.estado] = (acc[p.estado] || 0) + 1; });
    return acc;
  }, [pedidosHoy]);

  // ---------- Render ----------
  if (showKitchen) {
    return (
      <div className="h-full flex flex-col" style={{ background: 'var(--surface)' }}>
        <div
          className="flex items-center gap-3 flex-shrink-0"
          style={{ padding: '14px clamp(16px, 2vw, 28px)', borderBottom: '1.5px solid var(--line)' }}
        >
          <button
            onClick={() => setShowKitchen(false)}
            className="sk-btn sk-btn--ghost"
            style={{ minHeight: 44, padding: '10px 18px', fontSize: 14}}
          >
            {tx('volverAlPanel')}
          </button>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--ink)' }}>
            {tx('vistaCocina')}
          </h2>
        </div>
        <div className="flex-1 min-h-0 overflow-auto">
          <KitchenDashboard branch={branch} />
        </div>
      </div>
    );
  }

  return (
    <div className="pb-32 max-w-screen-2xl mx-auto" style={{ background: 'var(--surface)' }}>
      <ModuleHeader
        title={`${tx('buenDia')}${user?.name ? `, ${user.name.split(' ')[0]}` : ''}`}
        icon={LayoutDashboard}
        stats={[
          { n: loading ? '…' : fmtCOP(kpis.ventasTot),                                                       label: tx('ventasHoy'),    tone: 'ok' },
          { n: loading ? '…' : kpis.ventasCount ? fmtCOP(kpis.ticketProm) : '—',                              label: tx('ticketProm'),  tone: 'blue' },
          { n: loading ? '…' : String(kpis.pedidosActivos),                                                   label: tx('pedidosActivos'), tone: 'warn' },
          { n: loading ? '…' : kpis.mesasTot ? `${kpis.mesasOcupadas}/${kpis.mesasTot}` : '—',               label: tx('mesasOcupadas') },
        ]}
        extraActions={[
          { label: tx('vistaCocina'), icon: Eye, onClick: () => setShowKitchen(true), variant: 'ghost' },
        ]}
      />

      <div className="px-4 sm:px-6 lg:px-8">
        <p style={{ color: 'var(--muted-ink)', fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
          {branch} · {new Date().toLocaleDateString(localeFechas(), { timeZone: 'America/Bogota',  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>

      {err && (
        <div
          className="mb-4 p-3"
          style={{
            background: 'var(--danger-bg)', color: 'var(--danger-ink)',
            border: '1.5px solid color-mix(in srgb, var(--danger) 25%, var(--line))',
            borderRadius: 'var(--r-sm)', fontWeight: 600,
          }}
        >
          {err}
        </div>
      )}

      {/* ALERTA DE INVENTARIO — banner clickeable si hay items críticos/agotados o bajo mínimo.
          Click → navega a /inventory con el filtro "bajo mínimo" preactivado. */}
      {(() => {
        const agotados = inventario.filter(i => Number(i.cantidad) <= 0).length;
        const criticos = inventario.filter(i => {
          const c = Number(i.cantidad); const m = Number(i.stock_minimo);
          return c > 0 && c < m * 0.5;
        }).length;
        const bajoMin = inventario.filter(i => {
          const c = Number(i.cantidad); const m = Number(i.stock_minimo);
          return c > 0 && c >= m * 0.5 && c <= m;
        }).length;
        const totalAlerta = agotados + criticos + bajoMin;
        if (totalAlerta === 0) return null;
        const isCrit = agotados + criticos > 0;
        return (
          <button
            onClick={() => navigate('/inventory')}
            className="w-full press text-left mb-4 flex items-center gap-3"
            style={{
              padding: '14px 18px',
              background: isCrit ? 'var(--danger-bg)' : 'var(--warn-bg)',
              border: `1.5px solid ${isCrit ? 'var(--danger)' : 'var(--warn)'}`,
              borderRadius: 'var(--r-md)',
              cursor: 'pointer',
            }}
          >
            <AlertTriangle
              className="w-6 h-6 shrink-0"
              style={{ color: isCrit ? 'var(--danger-ink)' : 'var(--warn-ink)' }}
              strokeWidth={2.2}
            />
            <div className="flex-1 min-w-0">
              <div style={{ fontSize: 15, fontWeight: 800, color: isCrit ? 'var(--danger-ink)' : 'var(--warn-ink)' }}>
                {isCrit ? tx('inventarioCritico') : tx('inventarioBajoMinimo')}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginTop: 2 }}>
                {agotados > 0 && <span>{tx(agotados !== 1 ? 'agotadosN' : 'agotadoN', { n: agotados })}</span>}
                {criticos > 0 && <span>{tx(criticos !== 1 ? 'criticosN' : 'criticoN', { n: criticos })}</span>}
                {bajoMin > 0 && <span>{tx('bajoMinimoN', { n: bajoMin })}</span>}
                <span style={{ color: 'var(--muted-ink)' }}>{tx('clickGestionar')}</span>
              </div>
            </div>
          </button>
        );
      })()}

      {/* KPI CARDS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          icon={<TrendingUp className="w-5 h-5" />}
          label={tx('ventasHoy')}
          value={loading ? '…' : fmtCOP(kpis.ventasTot)}
          sub={`${kpis.ventasCount} ${kpis.ventasCount === 1 ? tx('factura') : tx('facturas')}`}
          tone="ok"
        />
        <KpiCard
          icon={<ReceiptIcon className="w-5 h-5" />}
          label={tx('ticketPromedio')}
          value={loading ? '…' : kpis.ventasCount ? fmtCOP(kpis.ticketProm) : '—'}
          sub={kpis.ventasCount ? tx('porCuentaCerrada') : tx('sinVentasHoy')}
          tone="blue"
        />
        <KpiCard
          icon={<Utensils className="w-5 h-5" />}
          label={tx('pedidosActivos')}
          value={loading ? '…' : String(kpis.pedidosActivos)}
          sub={Object.entries(pedidosPorEstado)
            .filter(([k]) => k !== 'pagado' && k !== 'cancelado')
            .map(([k, v]) => `${v} ${ESTADO_CRUDO[k] ? tx(ESTADO_CRUDO[k]) : k}`).join(' · ') || tx('sinPedidosActivos')}
          tone="warn"
        />
        <KpiCard
          icon={<UsersIcon2 className="w-5 h-5" />}
          label={tx('mesasOcupadas')}
          value={loading ? '…' : kpis.mesasTot ? `${kpis.mesasOcupadas} / ${kpis.mesasTot}` : '—'}
          sub={kpis.mesasTot
            ? tx('pctOcupacion', { pct: Math.round(kpis.mesasOcupadas / kpis.mesasTot * 100) })
            : tx('sinMesasCargadas')}
          tone="navy"
        />
      </div>

      {/* GRÁFICOS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Línea: ventas por hora */}
        <ChartCard
          className="lg:col-span-2"
          title={tx('ventasPorHora')}
          subtitle={kpis.ventasCount
            ? tx('facturasRegistradasHoy', { n: kpis.ventasCount })
            : tx('sinFacturasHoy')}
        >
          {kpis.ventasCount === 0 ? (
            <Empty label={tx('faltanVentas')} />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={ventasPorHora} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" vertical={false} />
                <XAxis dataKey="hora" tick={{ fill: 'var(--muted-ink)', fontSize: 11, fontWeight: 700 }} axisLine={{ stroke: 'var(--line)' }} tickLine={false} />
                <YAxis tick={{ fill: 'var(--muted-ink)', fontSize: 11}} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1_000_000 ? `${(v/1_000_000).toFixed(1)}M` : v >= 1000 ? `${Math.round(v/1000)}k` : String(v)} />
                <Tooltip
                  formatter={(v: number) => [fmtCOP(v), tx('ventas')]}
                  contentStyle={{ background: 'var(--ink-900)', border: 0, borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 700 }}
                  labelStyle={{ color: 'rgba(255,255,255,.7)' }}
                />
                <Line type="monotone" dataKey="total" stroke="var(--blue-600)" strokeWidth={2.5} dot={{ r: 3, fill: 'var(--blue-600)' }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* Barras: top productos */}
        <ChartCard
          title={tx('top5')}
          subtitle={topItems.length ? tx('porUnidadesHoy') : tx('sinDatos')}
        >
          {topItems.length === 0 ? (
            <Empty label={tx('faltanProductos')} />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={topItems} layout="vertical" margin={{ top: 5, right: 16, left: 4, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" horizontal={false} />
                <XAxis type="number" tick={{ fill: 'var(--muted-ink)', fontSize: 11}} axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="nombre" tick={{ fill: 'var(--ink)', fontSize: 11, fontWeight: 700 }} axisLine={false} tickLine={false} width={110} />
                <Tooltip
                  formatter={(v: number) => [tx('nUnidades', { n: v }), tx('cantidad')]}
                  contentStyle={{ background: 'var(--ink-900)', border: 0, borderRadius: 8, color: '#fff', fontSize: 12}}
                />
                <Bar dataKey="cantidad" fill="var(--blue-500)" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Cards inferiores: pedidos por estado + ocupación */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title={tx('pedidosPorEstado')} subtitle={tx('pasoDelFlujo')}>
          {kpis.pedidosActivos === 0 ? (
            <Empty label={tx('sinPedidosActivosAhora')} />
          ) : (
            <div className="flex flex-wrap gap-2">
              {[
                ['abierto', tx('estadoAbierto'), 'var(--warn-bg)', 'var(--warn-ink)'],
                ['en_preparacion', tx('estadoEnPreparacion'), 'var(--blue-100)', 'var(--blue-700)'],
                ['listo', tx('estadoListo'), 'var(--ok-bg)', 'var(--ok-ink)'],
                ['entregado', tx('estadoEntregado'), 'var(--bg-app)', 'var(--ink-700)'],
                ['facturado', tx('estadoPrefactura'), '#F1EAFE', '#6D28D9'],
              ].map(([k, lab, bg, fg]) => {
                const n = pedidosPorEstado[k as string] || 0;
                if (n === 0) return null;
                return (
                  <span
                    key={k as string}
                    className="inline-flex items-center gap-2 tabular-nums"
                    style={{
                      background: bg as string, color: fg as string,
                      padding: '10px 16px', borderRadius: 'var(--r-pill)',
                      fontSize: 14, fontWeight: 700,
                    }}
                  >
                    <b style={{ fontSize: 18, fontWeight: 800 }}>{n}</b>
                    {lab as string}
                  </span>
                );
              })}
            </div>
          )}
        </ChartCard>

        <ChartCard title={tx('ocupacionMesas')} subtitle={kpis.mesasTot ? tx('mesasOcupadasDe', { o: kpis.mesasOcupadas, t: kpis.mesasTot }) : tx('sinMesasRegistradas')}>
          {kpis.mesasTot === 0 ? (
            <Empty label={tx('noHayMesasSede')} />
          ) : (
            <div className="flex items-center gap-6 flex-wrap">
              {/* Gauge circular SVG */}
              {(() => {
                const pct = kpis.mesasOcupadas / kpis.mesasTot;
                const r = 56, c = 2 * Math.PI * r;
                return (
                  <svg width="140" height="140" viewBox="0 0 140 140">
                    <circle cx="70" cy="70" r={r} fill="none" stroke="var(--line)" strokeWidth="14" />
                    <circle
                      cx="70" cy="70" r={r}
                      fill="none"
                      stroke="var(--blue-600)"
                      strokeWidth="14"
                      strokeLinecap="round"
                      strokeDasharray={c}
                      strokeDashoffset={c * (1 - pct)}
                      transform="rotate(-90 70 70)"
                      style={{ transition: 'stroke-dashoffset .5s' }}
                    />
                    <text x="70" y="68" textAnchor="middle" fontSize="28" fontWeight="800" fill="var(--ink)" className="tabular-nums">
                      {Math.round(pct * 100)}%
                    </text>
                    <text x="70" y="86" textAnchor="middle" fontSize="11" fontWeight="700" fill="var(--muted-ink)">
                      {tx('ocupadas')}
                    </text>
                  </svg>
                );
              })()}
              <div className="flex flex-col gap-2 flex-1 min-w-[180px]">
                <RowMini label={tx('libres')} n={mesasState.filter(m => m.estado === 'libre').length} color="var(--ok)" />
                <RowMini label={tx('conPedido')} n={mesasState.filter(m => m.estado === 'ocupada' && m.pedido_estado !== 'entregado' && m.pedido_estado !== 'facturado').length} color="var(--blue-500)" />
                <RowMini label={tx('porCobrar')} n={mesasState.filter(m => m.estado === 'ocupada' && (m.pedido_estado === 'entregado' || m.pedido_estado === 'facturado')).length} color="var(--warn)" />
                <RowMini label={tx('reservadas')} n={mesasState.filter(m => m.estado === 'reservada').length} color="var(--muted-2)" />
              </div>
            </div>
          )}
        </ChartCard>
      </div>
      </div>
    </div>
  );
}

// ----- piezas del dashboard -----
function KpiCard({ icon, label, value, sub, tone }: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  tone: 'ok' | 'blue' | 'warn' | 'navy';
}) {
  const tones = {
    ok:   { bg: 'var(--ok-bg)',    ink: 'var(--ok-ink)' },
    blue: { bg: 'var(--blue-100)', ink: 'var(--blue-700)' },
    warn: { bg: 'var(--warn-bg)',  ink: 'var(--warn-ink)' },
    navy: { bg: 'var(--ink-900)',  ink: '#fff' },
  }[tone];
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1.5px solid var(--line)',
        borderRadius: 'var(--r-lg)',
        boxShadow: 'var(--sh-sm)',
        padding: 18,
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="sk-label" style={{ fontSize: 11, color: 'var(--muted-ink)' }}>{label}</span>
        <span
          className="flex items-center justify-center"
          style={{
            width: 36, height: 36, borderRadius: 10,
            background: tones.bg, color: tones.ink,
          }}
        >{icon}</span>
      </div>
      <div
        className="tabular-nums tracking-tight"
        style={{
          fontSize: 'clamp(20px, 6vw, 28px)',
          fontWeight: 800, color: 'var(--ink)', letterSpacing: '-.02em',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
        title={typeof value === 'string' ? value : undefined}
      >
        {value}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted-ink)', marginTop: 4 }}>
        {sub}
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children, className }: {
  title: string; subtitle?: string; children: React.ReactNode; className?: string;
}) {
  return (
    <div
      className={className}
      style={{
        background: 'var(--surface)',
        border: '1.5px solid var(--line)',
        borderRadius: 'var(--r-lg)',
        boxShadow: 'var(--sh-sm)',
        padding: 18,
      }}
    >
      <div className="mb-3">
        <h3 className="m-0" style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink)' }}>{title}</h3>
        {subtitle && (
          <p className="m-0 mt-1" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--muted-ink)' }}>{subtitle}</p>
        )}
      </div>
      {children}
    </div>
  );
}

function Empty({ label }: { label: string }) {
  return (
    <div
      className="flex items-center justify-center text-center"
      style={{
        minHeight: 220, color: 'var(--muted-ink)',
        fontSize: 14, fontWeight: 600,
        padding: 16,
      }}
    >
      {label}
    </div>
  );
}

function RowMini({ label, n, color }: { label: string; n: number; color: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
      <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: 'var(--ink-700)' }}>{label}</span>
      <b className="tabular-nums" style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink)' }}>{n}</b>
    </div>
  );
}
